import React, { useCallback, useMemo } from 'react';
import { Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useToastStore } from '@/store';
import Icon from '@/components/Icon';
import useRequireAuth from '@/hooks/useRequireAuth';
import { trackEvent } from '@/constants/utils/analytics';
import { mediumTap } from '@/constants/utils/haptics';

/**
 * Heart to favorite a deck recipe (`DeckTemplate`). Stops press
 * propagation. Anonymous visitors are routed through the shared
 * `useRequireAuth` hook so they see the unified `AuthCTAModal`.
 */
function DeckRecipeTemplateFavoriteHeart({ template, hitSlop = 10 }) {
  const { colors } = useTheme();
  const { addToast } = useToastStore(s => s.actions);
  const hasUser = useAuthStore(s => Boolean(s.user?.id));
  const favoriteDeckTemplateIds = useAuthStore(s => s.user?.favoriteDeckTemplateIds);
  const toggleFavoriteDeckTemplate = useAuthStore(s => s.toggleFavoriteDeckTemplate);
  const requireAuth = useRequireAuth();

  const templateId = template?.id != null ? String(template.id) : '';
  const isFavorited = !!(templateId && (favoriteDeckTemplateIds || []).map(String).includes(templateId));

  const styles = useMemo(() => StyleSheet.create({
    hit: {
      padding: 6,
      borderRadius: 8,
      justifyContent: 'center',
      alignItems: 'center',
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    },
  }), []);

  const onPress = useCallback(async (e) => {
    e?.stopPropagation?.();
    if (!templateId) return;
    const ran = requireAuth(
      async () => {
        try {
          const nowFavorited = await toggleFavoriteDeckTemplate(templateId);
          mediumTap();
          trackEvent('decks', nowFavorited ? 'deck_recipes_favorite' : 'deck_recipes_unfavorite', templateId);
        } catch (_) {
          addToast('Could not update favorite', 'error');
        }
      },
      {
        source: 'deck_recipes_favorite_heart',
        title: 'Sign in to favorite recipes',
        subtitle: 'Favorites stay tied to your account so they follow you across devices.',
        icon: 'heart',
      }
    );
    if (ran === undefined) {
      trackEvent('decks', 'deck_recipes_favorite_auth_required', templateId);
    }
  }, [templateId, requireAuth, toggleFavoriteDeckTemplate, addToast]);

  // Favoriting is account-bound; logged-out visitors don't see the control.
  if (!templateId || !hasUser) return null;

  return (
    <Pressable
      onPress={onPress}
      hitSlop={hitSlop}
      style={({ hovered, pressed }) => [
        styles.hit,
        (hovered || pressed) && { backgroundColor: colors.solid_color_tertiary },
      ]}
      accessibilityRole="button"
      accessibilityLabel={isFavorited ? 'Remove recipe from favorites' : 'Save recipe to favorites'}
      accessibilityState={{ selected: isFavorited }}
    >
      <Icon
        name="heart"
        size={15}
        solid={isFavorited}
        color={isFavorited ? '#ef4444' : colors.text_color_tertiary}
      />
    </Pressable>
  );
}

export default React.memo(DeckRecipeTemplateFavoriteHeart);
